/**
 * PWA install helper.
 *
 * - On Android Chrome / Edge / desktop Chrome: captures the `beforeinstallprompt`
 *   event and lets the UI trigger a native install dialog.
 * - On iOS Safari: no programmatic install API exists; the UI must show a
 *   manual guide ("share → 添加到主屏幕").
 * - Other browsers (iOS Chrome/Firefox in particular) cannot install PWAs at
 *   all — UI should hide the entry.
 */

export type Platform = 'android-chrome' | 'desktop-chrome' | 'ios-safari' | 'unsupported'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    listeners.forEach(fn => fn())
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    listeners.forEach(fn => fn())
  })
}

export function onInstallStateChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** True when the app is currently launched as an installed PWA (standalone). */
export function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  // iOS Safari exposes a non-standard navigator.standalone
  const navAny = window.navigator as Navigator & { standalone?: boolean }
  return navAny.standalone === true
}

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'unsupported'
  const ua = window.navigator.userAgent
  const isIOS = /iPhone|iPad|iPod/.test(ua)
  // On iOS, only Safari supports installs. Chrome/Firefox on iOS use a CriOS/FxiOS UA marker.
  if (isIOS) {
    const isSafari = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
    return isSafari ? 'ios-safari' : 'unsupported'
  }
  const isAndroid = /Android/.test(ua)
  const isChromeFamily = /Chrome|CriOS|Edg|OPR/.test(ua)
  if (isAndroid && isChromeFamily) return 'android-chrome'
  if (isChromeFamily) return 'desktop-chrome'
  return 'unsupported'
}

/** Whether a native install prompt is currently available (Android/desktop only). */
export function canPromptNativeInstall(): boolean {
  return deferredPrompt !== null
}

/** Triggers the native install dialog. Returns the user's choice. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable'
  await deferredPrompt.prompt()
  const choice = await deferredPrompt.userChoice
  deferredPrompt = null
  listeners.forEach(fn => fn())
  return choice.outcome
}

/** Register the minimal service worker so Chrome considers the app installable. */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  // Only register in production builds — vite dev server doesn't serve sw.js
  // at the right scope and a stale SW can break HMR.
  if (import.meta.env.DEV) return
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('SW registration failed:', err)
    })
  })
}
